import React, { useState } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { useAuth } from '../../hooks/useAuth';
import { API_CONFIG } from '../../config/api';

interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginFormProps {
  onSuccess?: () => void;
}

/**
 * LoginForm molecule component
 * Handles user login with validation and error handling
 */
export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: API_CONFIG.DEFAULT_CREDENTIALS.username,
    password: API_CONFIG.DEFAULT_CREDENTIALS.password,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<Partial<LoginCredentials>>({});

  const { login } = useAuth();

  // Form validation
  const validateForm = (): boolean => {
    const errors: Partial<LoginCredentials> = {};

    if (!credentials.username) {
      errors.username = 'Username is required';
    }

    if (!credentials.password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await login(credentials);
      setMessage({ type: 'success', text: 'Login successful!' });
      onSuccess?.();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Login failed.' });
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof LoginCredentials) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCredentials(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ 
        width: '100%', 
        maxWidth: 450,
        backgroundColor: 'white',
        p: 5,
        borderRadius: 3,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            color: 'primary.main',
            mb: 1,
          }}
        >
          Welcome to SmartHire
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          AI-Powered Recruitment Platform
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sign in to manage your hiring process
        </Typography>
      </Box>
      
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}
      
      <Box sx={{ mb: 2 }}>
        <Input
          label="Username"
          type="text"
          name="username"
          value={credentials.username}
          onChange={handleInputChange('username')}
          error={!!validationErrors.username}
          helperText={validationErrors.username}
          autoComplete="username"
          autoFocus
        />
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Input
          label="Password"
          type="password"
          name="password"
          value={credentials.password}
          onChange={handleInputChange('password')}
          error={!!validationErrors.password}
          helperText={validationErrors.password}
          autoComplete="current-password"
        />
      </Box>
      
      <Button
        type="submit"
        variant="primary"
        fullWidth
        loading={loading}
        size="large"
        sx={{ mb: 3, py: 1.5 }}
      >
        Sign In to Dashboard
      </Button>
      
      <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Demo Credentials:
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          Username: <strong>HRUser</strong> | Password: <strong>deloitte123</strong>
        </Typography>
      </Box>
    </Box>
  );
};